import { FIND_REGIONAL_BY_LOCATIONS } from "./index";

/**
 * Supported application languages.
 *
 * NOTE:
 * When a new language is introduced:
 *
 * 1. Add the language code to SUPPORTED_LANGUAGES.
 *    Example:
 *    const SUPPORTED_LANGUAGES = ["en", "ja", "th"] as const;
 *
 * 2. Add the translation file in LocalizationWrapper.
 *
 * 3. Add the language to REGION_LANGUAGES where supported.
 */
export const SUPPORTED_LANGUAGES = ["en", "ja"] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Region language configuration.
 *
 * The first language in the array is treated as the
 * default language for the region.
 *
 * Future examples:
 *
 * asean: ["en", "th"]
 * europe: ["en", "fr"]
 *
 * If a user's preferred language is not supported,
 * the application automatically falls back to the
 * region's default language.
 */
const REGION_LANGUAGES: Record<string, Language[]> = {
  global: ["en", "ja"],
  nml: ["en", "ja"],
  africa: ["en"],
  asia: ["en"],
  europe: ["en"],
  "middle-east": ["en"],
  americas: ["en"],
  "australia-newzealand": ["en"],
  india: ["en"],
};

/**
 * Returns the list of languages supported by
 * the current region.
 */
export const getSupportedLanguages = (): Language[] => {
  const currentRegion = FIND_REGIONAL_BY_LOCATIONS(
    globalThis.location.pathname,
  );

  if (!currentRegion) {
    return REGION_LANGUAGES.global;
  }

  return REGION_LANGUAGES[currentRegion.region] ?? REGION_LANGUAGES.global;
};

/**
 * Returns the user's preferred language.
 *
 * Priority:
 * 1. Previously selected language stored in localStorage.
 * 2. Browser language detection for first-time visitors.
 *
 * Future language support:
 *
 * Example:
 * SUPPORTED_LANGUAGES = ["en", "ja", "th"]
 *
 * Browser:
 * th-TH -> th
 * ja-JP -> ja
 * en-US -> en
 *
 * No additional code changes are required in this function
 * when a new language is added to SUPPORTED_LANGUAGES.
 */
export const getPreferredLanguage = (): Language => {
  const storedLanguage = localStorage.getItem("locale");

  if (
    storedLanguage &&
    SUPPORTED_LANGUAGES.includes(storedLanguage as Language)
  ) {
    return storedLanguage as Language;
  }

  const browserLanguage =
    navigator.language?.toLowerCase().split("-")[0] ?? "en";

  const detectedLanguage: Language =
    browserLanguage && SUPPORTED_LANGUAGES.includes(browserLanguage as Language)
      ? (browserLanguage as Language)
      : "en";

  localStorage.setItem("locale", detectedLanguage);

  return detectedLanguage;
};

/**
 * Resolves the language for the current region.
 *
 * If the preferred language is supported by the region,
 * it is displayed.
 *
 * Otherwise, the region's default language
 * (first item in REGION_LANGUAGES) is displayed.
 *
 * Examples:
 *
 * Preferred Language = ja
 * Region = Africa ["en"]
 * Result = en
 *
 * Preferred Language = ja
 * Region = Global ["en", "ja"]
 * Result = ja
 */
export const resolveLanguage = (): Language => {
  const preferredLanguage = getPreferredLanguage();
  const supportedLanguages = getSupportedLanguages();

  return supportedLanguages.includes(preferredLanguage)
    ? preferredLanguage
    : supportedLanguages[0];
};
